import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiMultipartRequest, apiRequest } from '../api/client';
import { disconnectSocket } from '../socket';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const syncUser = useCallback((nextUser) => {
    setUser(nextUser);
    if (!nextUser) {
      disconnectSocket();
    }
    return nextUser;
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    const body = await apiRequest('/api/auth/me');
    return syncUser(body.user || null);
  }, [syncUser]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      return await fetchCurrentUser();
    } catch (error) {
      syncUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchCurrentUser, syncUser]);

  const login = useCallback(async ({ username, password }) => {
    disconnectSocket();
    try {
      const body = await apiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password })
      });
      return syncUser(body.user || null);
    } catch (error) {
      syncUser(null);
      throw error;
    }
  }, [syncUser]);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } finally {
      syncUser(null);
    }
  }, [syncUser]);

  const uploadAvatar = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);

    const body = await apiMultipartRequest('/api/auth/avatar', {
      method: 'POST',
      body: formData
    });

    return syncUser(body.user || null);
  }, [syncUser]);

  const deleteAvatar = useCallback(async () => {
    await apiRequest('/api/auth/avatar', { method: 'DELETE' });
    return syncUser(user ? {
      ...user,
      hasAvatar: false,
      avatarVersion: null,
      avatarUrl: ''
    } : user);
  }, [syncUser, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    refresh,
    uploadAvatar,
    deleteAvatar
  }), [user, loading, login, logout, refresh, uploadAvatar, deleteAvatar]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 内使用');
  }
  return context;
}
